import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { Release } from '../types';
import { useAppContext } from '../context/AppContext';
import { useReleases } from '../context/ReleasesContext';
import { Link } from 'react-router-dom';

const RELEASES_PER_PAGE = 9;

const New: React.FC = () => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursorDate, setCursorDate] = useState<string | null>(null);
  const [cursorId, setCursorId] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [hasFollowedLabels, setHasFollowedLabels] = useState<boolean | null>(null);
  const { userId } = useAppContext();
  const { toggleListen, getSessionToggle, loadingReleaseId } = useReleases();

  // Helper to determine if a release is listened
  // For New page, server only returns unlistened releases, so default is false
  const isReleaseListened = useCallback((releaseId: number) => {
    return getSessionToggle(releaseId) ?? false;
  }, [getSessionToggle]);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastReleaseElementRef = useRef<HTMLDivElement | null>(null);

  const fetchLatestReleases = useCallback(async (isFirstLoad: boolean, fetchCursorDate: string | null, fetchCursorId: number | null): Promise<void> => {
    try {
      if (isFirstLoad) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const { data: newReleases, error: releasesError } = await supabase
        .rpc('get_latest_releases', {
          user_id: userId,
          page_size: RELEASES_PER_PAGE,
          cursor_date: fetchCursorDate,
          cursor_id: fetchCursorId,
        });

      if (releasesError) {
        console.error('RPC error:', releasesError);
        throw releasesError;
      }

      if (!newReleases || newReleases.length === 0) {
        setHasMore(false);
        if (isFirstLoad) {
          setReleases([]);
        }
        return;
      }

      if (newReleases.length < RELEASES_PER_PAGE) {
        setHasMore(false);
      }

      const lastItem = newReleases[newReleases.length - 1];
      setCursorDate(lastItem.release_date);
      setCursorId(lastItem.id);

      setReleases(prev => isFirstLoad ? newReleases : [...prev, ...newReleases]);

    } catch (error) {
      console.error('Error fetching releases:', error);
      if (error instanceof Error) {
        setError('Failed to load releases: ' + error.message);
      } else {
        setError('Failed to load releases: ' + String(error));
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId]);

  const handleToggleCheck = useCallback(async (releaseId: number) => {
    // We're now just using the centralized toggleListen from context
    try {
      // Pass current listened state so toggle knows which direction to go
      const currentlyListened = isReleaseListened(releaseId);
      await toggleListen(releaseId, currentlyListened);
    } catch (err) {
      // Error handling is now done in context too
      console.error('Error in handleToggleCheck:', err);
    }
  }, [toggleListen, isReleaseListened]);

  const loadMoreReleases = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      fetchLatestReleases(false, cursorDate, cursorId);
    }
  }, [loading, loadingMore, hasMore, cursorDate, cursorId, fetchLatestReleases]);

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    const currentObserver = observer.current;
    
    // Disconnect previous observer if it exists
    if (currentObserver) {
      currentObserver.disconnect();
    }
    
    // Create new observer
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreReleases();
      }
    }, { threshold: 0.1 });
    
    // Observe the last element
    if (lastReleaseElementRef.current) {
      observer.current.observe(lastReleaseElementRef.current);
    }
    
    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
      }
    };
  }, [hasMore, loadMoreReleases, releases.length]);

  useEffect(() => {
    const initializePage = async () => {
      if (!userId) return;

      try {
        // Quick count check to determine if user follows any labels
        const { count, error: countError } = await supabase
          .from('user_follow_labels')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId);

        if (countError) throw countError;

        const followsLabels = (count ?? 0) > 0;
        setHasFollowedLabels(followsLabels);

        if (followsLabels) {
          fetchLatestReleases(true, null, null);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Error initializing page:', error);
        setError('Failed to load. Please refresh the page.');
        setLoading(false);
      }
    };

    initializePage();
  }, [userId, fetchLatestReleases]);

  return (
    <div className="min-h-screen bg-violet-400">
      <main className="pt-16 pb-12 px-6 max-w-[1080px] mx-auto">
        <div className="mb-8 border-b border-violet-300 pb-4">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">New Releases</h1>
          <p className="text-gray-700">Discover the latest music from your followed labels</p>
        </div>
        
        {error && (
          <div className="text-red-600 mb-4 p-3 bg-red-50 rounded-lg border border-red-200">{error}</div>
        )}

        {loading && releases.length === 0 ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
          </div>
        ) : hasFollowedLabels === false ? (
          <div className="text-center py-10 bg-white rounded-xl shadow-sm border border-violet-100">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-violet-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <p className="text-gray-600 text-lg mb-4">
              Start following labels to see their latest releases here!
            </p>
            <Link 
              to="/labels"
              className="mt-2 inline-block px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors shadow-md"
            >
              Browse Labels
            </Link>
          </div>
        ) : releases.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-violet-100">
            <p className="text-gray-600 text-lg">No new releases available. Try following more labels!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {releases.map((release, index) => (
              <div 
                key={`${release.id}-${index}`} 
                className={`bg-white rounded-xl shadow-sm overflow-hidden border border-violet-100 transition-all hover:shadow-md ${
                  isReleaseListened(release.id!) ? 'ring-2 ring-emerald-400' : ''
                }`}
                ref={index === releases.length - 1 ? lastReleaseElementRef : null}
              >
                <div className="md:flex">
                  <div className="md:w-1/3 lg:w-1/4">
                    <div className="h-64 md:h-full bg-gradient-to-r from-violet-50 to-indigo-50 flex items-center justify-center p-4">
                      <a 
                        href={release.release_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="h-full w-full flex items-center justify-center"
                      >
                        <img 
                          src={release.image_url} 
                          alt={`${release.title} by ${release.artist}`} 
                          className="max-h-full max-w-full object-contain rounded shadow hover:opacity-90 transition-opacity"
                        />
                      </a>
                    </div>
                  </div>
                  <div className="p-5 md:w-2/3 lg:w-3/4 flex flex-col">
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-1">
                        <div className="text-xs font-semibold text-indigo-600 mb-1">
                          {(() => {
                            // Ensure we parse the date correctly by forcing local timezone interpretation
                            const dateStr = release.release_date;
                            const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
                            const date = new Date(year, month - 1, day); // months are 0-indexed in JS
                            
                            return date.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            });
                          })()}
                        </div>
                        <a 
                          href={release.labels?.label_url}
                          target="_blank"
                          rel="noopener noreferrer" 
                          className="px-2 py-1 bg-indigo-50 rounded text-xs text-indigo-700 hover:bg-indigo-100 transition-colors"
                        >
                          {release.labels?.label_name || ''}
                        </a>
                      </div>
                      
                      <a 
                        href={release.release_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:text-indigo-700 transition-colors"
                      >
                        <h2 className="text-xl font-bold text-gray-800 mb-1">{release.title}</h2>
                      </a>
                      <a 
                        href={release.release_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:text-indigo-600 transition-colors"
                      >
                        <h3 className="text-lg text-gray-600 mb-2">{release.artist}</h3>
                      </a>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        {release.genres && release.genres.map((genre, idx) => (
                          <span 
                            key={idx} 
                            className="text-xs px-2 py-1 bg-gray-50 text-gray-600 border border-gray-100 rounded"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end items-center pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleToggleCheck(release.id!)}
                        disabled={loadingReleaseId === release.id}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded transition-all duration-300 ease-in-out ${
                          isReleaseListened(release.id!) 
                            ? 'bg-emerald-50 text-emerald-700' 
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                        } ${
                          loadingReleaseId === release.id ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                      >
                        <div className="flex items-center transition-all duration-300 ease-in-out overflow-hidden">
                          {loadingReleaseId === release.id ? (
                            <>
                              <div className="h-4 w-4 border-t-2 border-b-2 border-current rounded-full animate-spin transition-opacity duration-300 ease-in-out"></div>
                              <span className="transition-opacity duration-300 ease-in-out ml-1">Saving...</span>
                            </>
                          ) : isReleaseListened(release.id!) ? (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transition-transform duration-300 ease-in-out" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="transition-all duration-300 ease-in-out ml-1">Listened</span>
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transition-transform duration-300 ease-in-out" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <circle cx="12" cy="12" r="9" strokeWidth={2} />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 12l3 3 5-5" />
                              </svg>
                              <span className="transition-all duration-300 ease-in-out ml-1">Mark as Listened</span>
                            </>
                          )}
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {loadingMore && (
          <div className="flex justify-center mt-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-violet-500"></div>
          </div>
        )}
      </main>
    </div>
  );
};

export default New; 